import { FC, ReactNode, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SearchContextProvider, useSearchContext } from "../../../../../context/searchContext";
import useI18n from "../../../../../hooks/useTranslations";
import { CustomError } from "../../../../../http/errors";
import { useVertigripSteelVersionsApi } from "../../../../../http/vertigrip/useSteelVersions";
import Header, { HeaderPath } from "../../../../ui/Header";
import Tabs, { Tab } from "../../../../ui/Tabs";
import CommonSteelVersion from "../../../globalSettings/steelVersions/CommonSteelVersion";
import { VERTIGRIP } from "../../AdminSystemSettings";
import SteelVersionIntermediateElements from "../intermediateElements/SteelVersionIntermediateElements";
import LowerElements from "../lowerElements/SteelVersionLowerElements";
import SteelVersionUpperExtensions from "../upperExtensions/SteelVersionUpperExtensions";
import Absorbers from "./absorbers/Absorbers";

const VertigripSteelVersion: FC = () => {
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const { steelVersionId } = useParams<{ steelVersionId: string }>();
  const vertigripSteelVersionsApi = useVertigripSteelVersionsApi();
  const aborter = useRef(new AbortController());
  const { data, error, trigger } = vertigripSteelVersionsApi.getAdminSteelVersion(aborter.current.signal);
  const [searchParams] = useSearchParams();
  const selectedIndex = Number(searchParams.get("tab")) || 0;

  useEffect(() => {
    steelVersionId && trigger(steelVersionId);
  }, [steelVersionId]);

  const tabs: Tab[] = [
    { title: i18n.translation.common.generalData },
    { title: i18n.translation.absorbers.plural },
    { title: i18n.translation.upperExtensions.plural },
    { title: i18n.translation.intermediateElements.plural },
    { title: i18n.translation.lowerElements.plural },
  ];

  const path = [
    { url: `/admin/systems`, display: i18n.translation.systems.all },
    { url: `/admin/systems/${systemId}`, display: VERTIGRIP },
    {
      url: `/admin/systems/${systemId}/settings?tab=3`,
      display: `${i18n.translation.common.settings}: ${i18n.translation.steelVersions.plural}`,
    },
    { display: data?.name ?? "" },
  ];

  const contents: ReactNode[] = [
    <Tabs.SubTab>
      <CommonSteelVersion
        isGlobalSettings={false}
        error={error}
        trigger={() => steelVersionId && trigger(steelVersionId)}
        data={data}
        steelVersionId={steelVersionId}
        updateSteelVersion={vertigripSteelVersionsApi.updateAdminSteelVersion}
      ></CommonSteelVersion>
    </Tabs.SubTab>,
    <Absorbers active={selectedIndex === 1} />,
    <SteelVersionUpperExtensions active={selectedIndex === 2} />,
    <SteelVersionIntermediateElements active={selectedIndex === 3} />,
    <LowerElements active={selectedIndex === 4} />,
  ];

  return (
    <SearchContextProvider>
      <VertigripSteelVersionContent
        title={`${i18n.translation.steelVersions.singular}: ${data?.name}`}
        error={error}
        path={path}
        tabs={tabs}
        contents={contents}
      />
    </SearchContextProvider>
  );
};

type VertigripSteelVersionContentProps = {
  title: string;
  error?: CustomError;
  path: HeaderPath[];
  tabs: Tab[];
  contents: ReactNode[];
};

const VertigripSteelVersionContent: FC<VertigripSteelVersionContentProps> = ({ title, error, path, tabs, contents }) => {
  const { actions, title: tabTitle } = useSearchContext();

  return (
    <>
      <Header title={tabTitle ? `${title} · ${tabTitle}` : title} error={error} path={path} buttons={actions} />
      <Tabs urlParameter="tab" tabs={tabs} contents={contents} />
    </>
  );
};

export default VertigripSteelVersion;
